/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

/* Publish functions for the Adobe Client Data Layer (ACDL) */

import { CartModel, Item } from '@/cart/data/models/cart-model';
import {
  transformItems,
  transformShoppingCart,
} from '@/cart/data/transforms/acdl-context';
import {
  ShoppingCartContext,
  ShoppingCartItem,
} from '../data/models/acdl-models';

/**
 * See: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/contexts.ts
 */
export const contexts = {
  SHOPPING_CART_CONTEXT: 'shoppingCartContext',
  PRODUCT_CONTEXT: 'productContext',
  CHANGED_PRODUCTS_CONTEXT: 'changedProductsContext',
};

/**
 * See: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/events.ts
 */
export const events = {
  // See: https://github.com/adobe/commerce-events/blob/main/examples/events/open-cart.md
  OPEN_CART: 'open-cart',
  // See: ??? (not documented in the commerce-events repo)
  //UPDATE_CART: 'update-cart',
  // See: https://github.com/adobe/commerce-events/blob/main/examples/events/add-to-cart.md
  ADD_TO_CART: 'add-to-cart',
  // See: https://github.com/adobe/commerce-events/blob/main/examples/events/remove-from-cart.md
  REMOVE_FROM_CART: 'remove-from-cart',
  // See: https://github.com/adobe/commerce-events/blob/main/examples/events/shopping-cart-view.md
  SHOPPING_CART_VIEW: 'shopping-cart-view',
  // See: https://github.com/adobe/commerce-events/blob/main/examples/events/initiate-checkout.md
  INITIATE_CHECKOUT: 'initiate-checkout',
};

export function getAdobeDataLayer() {
  // @ts-ignore
  window.adobeDataLayer = window.adobeDataLayer || [];
  // @ts-ignore
  return window.adobeDataLayer;
}

/**
 * Sets a context in the Adobe Client Data Layer (ACDL)
 * Logic based on: https://github.com/adobe/commerce-events/blob/main/packages/storefront-events-sdk/src/Base.ts#L6
 */
function setContext(name: string, data: any) {
  const adobeDataLayer = getAdobeDataLayer();

  // Clear existing context
  adobeDataLayer.push({
    [name]: null,
  });

  // Set new context
  adobeDataLayer.push({
    [name]: data,
  });
}

/**
 * Pushes an event to the Adobe Client Data Layer (ACDL)
 * Logic based on: https://github.com/adobe/commerce-events/blob/1973d0ce28471ef190fa06dad6359ffa0ab51db6/packages/storefront-events-sdk/src/Base.ts#L34
 */
function pushEvent(event: string, additionalContext?: any) {
  const adobeDataLayer = getAdobeDataLayer();

  adobeDataLayer.push((acdl: any) => {
    const state = acdl.getState ? acdl.getState() : {};

    acdl.push({
      event,
      eventInfo: {
        ...state,
        ...additionalContext,
      },
    });
  });
}

// Triggered when a new cart is created, which is when a product is added to an empty cart.
export function publishOpenCartEvent(
  cart: CartModel,
  addedItems: Item[],
  locale: string
) {
  const shoppingCartContext = transformShoppingCart({
    cart,
    locale,
  });

  // Set context and push open cart event
  setContext(contexts.SHOPPING_CART_CONTEXT, {
    ...shoppingCartContext,
  });

  // Transform items and set changed products context
  const shoppingCartItems = transformItems(addedItems, locale);
  
  // Set changed products context to include the items that opened the cart
  setContext(contexts.CHANGED_PRODUCTS_CONTEXT, {
    items: shoppingCartItems,
  });

  // Push the open cart event with contexts set
  pushEvent(events.OPEN_CART);

  // Set context and push add to cart event for each updated item
  shoppingCartItems.forEach((item) => {
    setContext(contexts.PRODUCT_CONTEXT, item.product);
    publishItemsUpdatedEvent(shoppingCartContext, [item], events.ADD_TO_CART);
  });
}

// Triggered when any cart page loads.
export function publishShoppingCartViewEvent(cart: CartModel, locale: string) {
  const shoppingCartContext = transformShoppingCart({
    cart,
    locale,
  });

  setContext(contexts.SHOPPING_CART_CONTEXT, {
    ...shoppingCartContext,
  });

  pushEvent(events.SHOPPING_CART_VIEW);
}

// A re-usable function to publish events when items are added to, or removed from the cart.
function publishItemsUpdatedEvent(
  shoppingCartContext: ShoppingCartContext,
  updatedItems: ShoppingCartItem[],
  event: string
) {
  const changedProductsContext = {
    items: updatedItems,
  };

  setContext(contexts.SHOPPING_CART_CONTEXT, {
    ...shoppingCartContext,
  });

  setContext(contexts.CHANGED_PRODUCTS_CONTEXT, {
    ...changedProductsContext,
  });

  pushEvent(event);
}

export function publishCartUpdateEvents(
  cart: CartModel,
  updatedItems: {
    uid: string;
  }[],
  locale: string
) {
  const newShoppingCartContext = transformShoppingCart({
    cart,
    locale,
  });

  const currentItems = newShoppingCartContext.items;

  const adobeDataLayer = getAdobeDataLayer();

  const currentState = adobeDataLayer.getState ? adobeDataLayer.getState() : {};

  const { shoppingCartContext: { items: previousItems = [] } = {} } =
    currentState;

  updatedItems.forEach((updatedItem) => {
    const previousItem = previousItems.find(
      (item: ShoppingCartItem) => item.id === updatedItem.uid
    );

    const currentItem = currentItems!.find(
      (item: ShoppingCartItem) => item.id === updatedItem.uid
    );

    if (!currentItem && !previousItem) {
      // Not enough data available to publish an event
      // Adobe Data Layer possibly not initialized yet
      return;
    }

    if (!previousItem && currentItem) {
      // Publish add to cart event for the item added
      setContext(contexts.PRODUCT_CONTEXT, currentItem.product);
      publishItemsUpdatedEvent(
        newShoppingCartContext,
        [currentItem],
        events.ADD_TO_CART
      );
    } else if (previousItem && !currentItem) {
      // Publish remove from cart event for the item removed
      setContext(contexts.PRODUCT_CONTEXT, previousItem.product);
      publishItemsUpdatedEvent(
        newShoppingCartContext,
        [previousItem],
        events.REMOVE_FROM_CART
      );
    } else if (currentItem!.quantity > previousItem.quantity) {
      // Publish add to cart event for the item increased
      setContext(contexts.PRODUCT_CONTEXT, currentItem!.product);
      publishItemsUpdatedEvent(
        newShoppingCartContext,
        [currentItem!],
        events.ADD_TO_CART
      );
    } else {
      // Publish remove from cart event for the item decreased
      setContext(contexts.PRODUCT_CONTEXT, currentItem!.product);
      publishItemsUpdatedEvent(
        newShoppingCartContext,
        [currentItem!],
        events.REMOVE_FROM_CART
      );
    }
  });
}

export function publishInitiateCheckoutEvent(cart: CartModel, locale?: string) {
  const shoppingCartContext = transformShoppingCart({
    cart,
    locale,
  });

  setContext(contexts.SHOPPING_CART_CONTEXT, {
    ...shoppingCartContext,
  });

  pushEvent(events.INITIATE_CHECKOUT);
}
